import { useEffect, useState } from 'react';
import { api, request, calls as Api, type Outcome, type Segment, type Side, type TradeCall, type TradeCallInput } from '@tvx/api-client';
import { Modal } from '../components/Modal';

interface CallMeta { segments: Segment[]; sides: Side[]; outcomes: Outcome[] }

const BLANK: TradeCallInput = {
  instrument: '', segment: 'Equity', side: 'Buy', entry: null, target: null, stop_loss: null,
  quantity_guide: '', timeframe: '', risk_reward: '', valid_till: '', rationale: '',
  chart_media_id: null, state: 'draft', outcome: 'open', outcome_pct: null, published_at: null, plan_tier: 'all',
};

interface CallPic { id: number; src: string; caption: string; at: string }
interface CallDetail { images: CallPic[]; updates: { kind: string; text: string; price: number | null; at: string }[] }
interface CallRef { id: number; instrument: string; outcome: string }

/** What the desk posted under a call from Telegram, with a way to fix a picture that landed on the wrong one. */
function Posted({ callId }: { callId: number }) {
  const [d, setD] = useState<CallDetail | null>(null);
  const [others, setOthers] = useState<CallRef[]>([]);
  const load = () => api.get<CallDetail>(`/calls/${callId}`).then(setD).catch(() => {});
  useEffect(() => {
    void load();
    void api.get<{ calls: CallRef[] }>('/calls').then((r) => setOthers(r.calls.filter((c) => c.id !== callId).slice(0, 40))).catch(() => {});
  }, [callId]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!d) return null;
  const label = (k: string) => ({ active: 'Active', booking: 'Booking', trail: 'SL moved', cancel: 'Order off', target_hit: 'Target hit', sl_hit: 'SL hit', closed: 'Closed', note: 'Note' }[k] ?? k);
  return (
    <div className="fld">
      <label className="lbl">Charts and updates under this call</label>
      <div style={{ margin: '0 0 10px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label className="b bo" style={{ cursor: busy ? 'wait' : 'pointer', padding: '6px 12px', fontSize: 12 }}>
          {busy ? 'Uploading…' : 'Add chart'}
          <input type="file" accept="image/*" multiple hidden disabled={busy}
            onChange={async (e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (!files.length) return;
              setBusy(true); setErr('');
              try {
                for (const f of files) {
                  const form = new FormData();
                  form.append('file', f);
                  await request(`/calls/${callId}/images`, { method: 'POST', body: form });
                }
                await load();
              } catch (x) { setErr((x as Error).message); }
              setBusy(false);
            }} />
        </label>
        <span style={{ fontSize: 11.5, color: 'var(--tx3)' }}>Shown to members at the top of the call&apos;s updates.</span>
        {err ? <span className="err" style={{ padding: '4px 8px' }}>{err}</span> : null}
      </div>
      {d.updates.length ? (
        <div style={{ display: 'grid', gap: 4, marginBottom: 10, fontSize: 12.5 }}>
          {d.updates.map((u, i) => (
            <div key={i} style={{ display: 'flex', gap: 10 }}>
              <span style={{ minWidth: 70, color: 'var(--goldtx)', fontWeight: 600 }}>{label(u.kind)}</span>
              <span style={{ flex: 1 }}>{u.price != null ? `${u.price} · ` : ''}{u.text}</span>
              <span style={{ color: 'var(--tx3)' }}>{u.at.slice(5, 16)}</span>
            </div>
          ))}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {d.images.map((im) => (
          <div key={im.id} style={{ width: 150, display: 'grid', gap: 6 }}>
            <a href={im.src} target="_blank" rel="noreferrer">
              <img src={im.src} alt="" style={{ width: '100%', borderRadius: 8, border: '1px solid var(--line)', display: 'block' }} />
            </a>
            <select className="inp" defaultValue="" style={{ padding: '5px 8px', fontSize: 11.5 }}
              onChange={(e) => { if (e.target.value) void api.put(`/calls/${callId}/images/${im.id}`, { call_id: Number(e.target.value) }).then(load); }}>
              <option value="">Move to…</option>
              {others.map((o) => <option key={o.id} value={o.id}>{o.instrument} · #{o.id}</option>)}
            </select>
            <button className="b bo" type="button" style={{ padding: '4px 8px', fontSize: 11.5 }}
              onClick={() => void api.del(`/calls/${callId}/images/${im.id}`).then(load)}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TradeCallForm({ call, onCancel, onSaved }: {
  call: TradeCall | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<TradeCallInput>(() => call ? { ...call } : BLANK);
  const [meta, setMeta] = useState<CallMeta | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { void api.get<CallMeta>('/meta').then(setMeta).catch(() => {}); }, []);

  const set = <K extends keyof TradeCallInput>(k: K, v: TradeCallInput[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));
  const numField = (v: string) => (v.trim() === '' ? null : Number(v));

  async function submit(state: 'draft' | 'published') {
    if (!f.instrument.trim()) { setError('An instrument is required.'); return; }
    setError('');
    setStatus(state === 'published' ? 'Publishing…' : 'Saving…');
    try {
      const body = { ...f, state };
      if (call) await Api.update(call.id, body);
      else await Api.create(body);
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  /* Risk:reward is arithmetic, so it is offered rather than asked for — but it
     stays a text field, because a call with an unusual structure should not be
     forced into a number this screen worked out. */
  const suggestion = (() => {
    if (f.entry == null || f.target == null || f.stop_loss == null) return null;
    const reward = Math.abs(f.target - f.entry);
    const risk = Math.abs(f.entry - f.stop_loss);
    if (!risk) return null;
    return `1:${(reward / risk).toFixed(1)}`;
  })();

  return (
    <Modal
      title={call ? `Edit call · ${call.instrument}` : 'New trade call'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>
            {call?.state === 'published' ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <Field label="Instrument">
          <input className="inp" value={f.instrument} autoFocus
            onChange={(e) => set('instrument', e.target.value.toUpperCase())} placeholder="RELIANCE" />
        </Field>
        <Field label="Segment">
          <select className="inp" value={f.segment} onChange={(e) => set('segment', e.target.value as Segment)}>
            {(meta?.segments ?? [f.segment]).map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Side">
          <select className="inp" value={f.side} onChange={(e) => set('side', e.target.value as Side)}>
            {(meta?.sides ?? [f.side]).map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <Field label="Entry">
          <input className="inp" type="number" step="any" value={f.entry ?? ''}
            onChange={(e) => set('entry', numField(e.target.value))} />
        </Field>
        <Field label="Target">
          <input className="inp" type="number" step="any" value={f.target ?? ''}
            onChange={(e) => set('target', numField(e.target.value))} />
        </Field>
        <Field label="Stop loss">
          <input className="inp" type="number" step="any" value={f.stop_loss ?? ''}
            onChange={(e) => set('stop_loss', numField(e.target.value))} />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <Field label="Timeframe">
          <input className="inp" value={f.timeframe} placeholder="Intraday"
            onChange={(e) => set('timeframe', e.target.value)} />
        </Field>
        <Field label="Risk : reward">
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="inp" value={f.risk_reward} placeholder={suggestion ?? '1:2'}
              onChange={(e) => set('risk_reward', e.target.value)} />
            {suggestion && f.risk_reward !== suggestion ? (
              <button className="b bo" type="button" style={{ padding: '9px 12px', fontSize: 11.5 }}
                onClick={() => set('risk_reward', suggestion)}>Use {suggestion}</button>
            ) : null}
          </div>
        </Field>
        <Field label="Valid till">
          <input className="inp" value={f.valid_till} placeholder="Close of day"
            onChange={(e) => set('valid_till', e.target.value)} />
        </Field>
      </div>


      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
        <Field label="Quantity guide">
          <input className="inp" value={f.quantity_guide} placeholder="Risk 1% of capital"
            onChange={(e) => set('quantity_guide', e.target.value)} />
        </Field>
        <Field label="Target Plan / Audience">
          <select className="inp" value={f.plan_tier || 'all'} onChange={(e) => set('plan_tier', e.target.value)}>
            <option value="all">All Trade Calls Members (Global)</option>
            <option value="equity">Equity Members Only</option>
            <option value="fno">F&O Members Only</option>
            <option value="forex">Forex Members Only</option>
          </select>
        </Field>
      </div>

      <Field label="Why this trade (members see this)">
        <textarea className="inp ta" rows={4} value={f.rationale}
          onChange={(e) => set('rationale', e.target.value)} />
      </Field>

      {call ? <Posted callId={call.id} /> : null}

      {call ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
          <Field label="Result">
            <select className="inp" value={f.outcome} onChange={(e) => set('outcome', e.target.value as Outcome)}>
              {(meta?.outcomes ?? [f.outcome]).map((o) => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
            </select>
          </Field>
          <Field label="Moved by (%)">
            <input className="inp" type="number" step="any" value={f.outcome_pct ?? ''}
              placeholder="blank if not measured"
              onChange={(e) => set('outcome_pct', numField(e.target.value))} />
          </Field>
        </div>
      ) : null}
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="fld"><label className="lbl">{label}</label>{children}</div>;
}
